import { Grid, IconButton, TextField } from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Edit as EditIcon } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { GradeChemistryLimit } from "../../../models/CatalogGrade";
import { upsertCatGradeChemistryLimit } from "../../../repositories/CatalogGradeRepository";

type Props = {
  label?: String;
  element: GradeChemistryLimit;
  disabled: boolean;
  refreshData: Function;
  setError: (error: string) => void;
  setNotificationMessage: (message: string) => void;
};

export const ElementLimits = ({
  label = "C",
  element,
  disabled,
  refreshData,
  setError,
  setNotificationMessage,
}: Props) => {
  const [fieldsEnabled, setFieldEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { control, handleSubmit, reset } = useForm<GradeChemistryLimit>({
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<GradeChemistryLimit> = async (data) => {
    setIsLoading(true);
    setError("");
    if (data.MinValue < 0 || data.MinValue > 100) {
      setError("MinValue must be between 0 and 100.");
      setIsLoading(false);
      return;
    }

    if (data.MaxValue < 0 || data.MaxValue > 100) {
      setError("MaxValue must be between 0 and 100.");
      setIsLoading(false);
      return;
    }

    if (data.MinValue > data.MaxValue) {
      setError("MinValue must be less than or equal to MaxValue.");
      setIsLoading(false);
      return;
    }
    if (data.AimValue !== null) {
      if (data.AimValue < data.MinValue || data.AimValue > data.MaxValue) {
        setError("AimValue must be between MinValue and MaxValue.");
        setIsLoading(false);
        return;
      }
    }

    const resp = await upsertCatGradeChemistryLimit({
      gradeId: data?.GradeId!,
      elementId: data.ElementId!,
      minValue: data.MinValue,
      maxValue: data.MaxValue,
      aimValue: data.AimValue,
    });
    setIsLoading(false);
    if (resp.ok) {
      setNotificationMessage("Chemistry limit saved successfully");
      await refreshData();
    } else {
      setError(resp.message!);
    }
    setFieldEnabled(false);
  };

  useEffect(() => {
    reset(element);
  }, [element]);

  return (
    <Grid container md={12} spacing={1}>
      <Grid item md={4}>
        <Controller
          name="ElementName"
          control={control}
          render={() => (
            <TextField
              variant="outlined"
              value={element.ElementName}
              size="small"
              fullWidth
              disabled
            />
          )}
        />
      </Grid>
      <Grid item md={2}>
        <Controller
          name="MinValue"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              variant="outlined"
              value={field.value}
              onChange={(e) => {
                field.onChange(
                  e.target.value === "" ? null : parseFloat(e.target.value),
                );
              }}
              size="small"
              fullWidth
              disabled={!fieldsEnabled}
              error={!!error}
              helperText={error?.message}
              type="number"
            />
          )}
        />
      </Grid>
      <Grid item md={2}>
        <Controller
          name="MaxValue"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              variant="outlined"
              value={field.value}
              onChange={(e) => {
                field.onChange(
                  e.target.value === "" ? null : parseFloat(e.target.value),
                );
              }}
              size="small"
              fullWidth
              disabled={!fieldsEnabled}
              error={!!error}
              helperText={error?.message}
              type="number"
            />
          )}
        />
      </Grid>
      <Grid item md={2}>
        <Controller
          name="AimValue"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              variant="outlined"
              value={field.value}
              onChange={(e) => {
                field.onChange(
                  e.target.value === "" ? null : parseFloat(e.target.value),
                );
              }}
              size="small"
              fullWidth
              disabled={!fieldsEnabled}
              error={!!error}
              helperText={error?.message}
              type="number"
            />
          )}
        />
      </Grid>
      <Grid item md={2}>
        {!fieldsEnabled ? (
          <IconButton
            onClick={() => setFieldEnabled(true)}
            title={`Edit '${label}'`}
            color="primary"
            disabled={disabled}
          >
            <EditIcon style={{ width: 25, height: 25 }} />
          </IconButton>
        ) : (
          <>
            <IconButton
              onClick={() => setFieldEnabled(false)}
              color="secondary"
            >
              <CloseIcon style={{ width: 25, height: 25 }} />
            </IconButton>
            <IconButton onClick={handleSubmit(onSubmit)} type="submit">
              <CheckIcon style={{ width: 25, height: 25, color: "green" }} />
            </IconButton>
          </>
        )}
      </Grid>
    </Grid>
  );
};
